import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { Notification } from '../types/meetings';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchNotifications: (unreadOnly?: boolean) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,

  fetchNotifications: async (unreadOnly: boolean = false) => {
    if (!isSupabaseConfigured()) {
      set({
        error: 'Supabase is not configured. Please connect to Supabase to access notifications.',
        isLoading: false
      });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ notifications: [], unreadCount: 0, isLoading: false });
        return;
      }

      let query = supabase
        .from('notifications_enhanced')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (unreadOnly) {
        query = query.eq('is_read', false);
      }

      const { data, error } = await query;
      if (error) throw error;

      const unreadCount = data?.filter(n => !n.is_read).length || 0;

      set({
        notifications: data || [],
        unreadCount,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  markAsRead: async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications_enhanced')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      const { notifications } = get();
      const updatedNotifications = notifications.map(n =>
        n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      );
      const unreadCount = updatedNotifications.filter(n => !n.is_read).length;

      set({
        notifications: updatedNotifications,
        unreadCount
      });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  markAllAsRead: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('notifications_enhanced')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      // Update local state
      const { notifications } = get();
      const now = new Date().toISOString();
      const updatedNotifications = notifications.map(n => ({
        ...n,
        is_read: true,
        read_at: now
      }));

      set({
        notifications: updatedNotifications,
        unreadCount: 0
      });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  deleteNotification: async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications_enhanced')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      const { notifications } = get();
      const updatedNotifications = notifications.filter(n => n.id !== id);
      const unreadCount = updatedNotifications.filter(n => !n.is_read).length;

      set({
        notifications: updatedNotifications,
        unreadCount
      });
    } catch (error: any) {
      set({ error: error.message });
    }
  }
}));